import { AnalyticsEvent, UIAnalyticsEvent } from '@atlaskit/analytics-next';
import AnalyticsWebClient, {
  apdexType,
  envType,
  OperationalEventPayload,
  originType,
  perimeterType,
  platformType,
  ScreenEventPayload,
  tenantType,
  TrackEventPayload,
  UIEventPayload,
  userType,
} from '@atlassiansox/analytics-web-client';
import Cookies from 'js-cookie';
import memoize from 'memoize-one';

import { getConfig, getEnv } from '../config';
import { referrerHostName } from '../facade/window';
import { PageContent } from '../model/PageContent';
import { captureException } from '../sentry/index';
import { ErrorSource } from '../sentry/types';
import Store from '../store';
import UserStore from '../store/userStore';

import { initUfo } from './UfoPublisherClient';

const WAC_FIELDS = [
  '__aid_user_id',
  'ajs_anonymous_id',
  'ajs_user_id',
  'ajs_group_id',
  'atlCohort',
  'optimizelyEndUserId',
  'seg_xid',
];

type WAC_ANALYTICS_CONTEXT = Partial<{
  __aid_user_id: string;
  ajs_anonymous_id: string;
  ajs_group_id: string;
  ajs_user_id: string;
  atlCohort: string;
  optimizelyEndUserId: string;
  seg_xid: string;
}>;

export type Subject = 'products' | 'quickSearch' | 'root';

const getAnalyticsContext = memoize(() => {
  const attributes = WAC_FIELDS.reduce<WAC_ANALYTICS_CONTEXT>((curr, next) => {
    curr[next as keyof WAC_ANALYTICS_CONTEXT] = Cookies.get(next);
    return curr;
  }, {});

  return attributes;
});

const { LOCAL, DEV, STAGING, PROD } = envType;
const ENV_MAP: { [key: string]: envType } = {
  ddev: DEV,
  local: LOCAL,
  test: LOCAL,
  'stg-fedm-east': STAGING,
  'prod-fedm-east': PROD,
};
const getEnvType = () => ENV_MAP[getEnv()] || PROD;

let contentLoadTime: { [key: string]: number } = {};

export default interface AnalyticsClient {
  fireEvent(event: UIAnalyticsEvent): void;
  getAnalyticsWebClient(): AnalyticsWebClient;
  getDefaultSource(): string;
  markContentLoad(pageContent: PageContent, isEmptyState: boolean): void;
  page(name: string, properties?: object): Promise<void>;
  startUIViewedEvent(): Promise<void>;
  track(subject: string, action: string, attributes?: object): Promise<void>;
  trackOperational(subject: string, action: string, attributes?: object): Promise<void>;
  ui(subject: string, action: string, attributes?: object, additionalPayload?: object): Promise<void>;
}

export class AnalyticsClientImpl implements AnalyticsClient {
  private analyticsWebClient: AnalyticsWebClient;
  private readyPromise: Promise<void>;
  private userStore: UserStore;

  constructor(store: Store, subproduct = 'web') {
    const { uiStore, userStore } = store;

    this.userStore = userStore;
    this.analyticsWebClient = new AnalyticsWebClient(
      {
        env: getEnvType(),
        origin: originType.WEB,
        platform: uiStore.mobileMode ? platformType.MOBILE_WEB : platformType.WEB,
        product: 'uchi',
        subproduct,
        perimeter: perimeterType.FEDRAMP_MODERATE,
      },
      {
        apiHost: getConfig().gasApiHost,
        flushBeforeUnload: true,
      },
    );
    this.readyPromise = this.initialiseAnalytics();
    this.initaliseUfo();
  }

  fireEvent = (event: AnalyticsEvent) => {
    try {
      if (this.analyticsWebClient) {
        const { payload = {} } = event;
        const { eventType } = payload;

        payload.attributes = Object.assign({}, payload.attributes, this.getAdditionalContext());

        switch (eventType) {
          case 'operational':
            this.analyticsWebClient.sendOperationalEvent(payload as OperationalEventPayload);
            break;
          case 'ui':
            this.analyticsWebClient.sendUIEvent(payload as UIEventPayload);
            break;
          case 'track':
            this.analyticsWebClient.sendTrackEvent(payload as TrackEventPayload);
            break;
          case 'screen':
            this.analyticsWebClient.sendScreenEvent(payload as ScreenEventPayload);
            break;
          default:
            throw new Error(`Unknown eventType ${eventType}`);
        }
      }
    } catch (e) {
      captureException(e, { source: ErrorSource.ANALYTICS_CLIENT });
    }
  };

  getAnalyticsWebClient() {
    return this.analyticsWebClient;
  }

  getDefaultSource(): string {
    return 'home';
  }

  /**
   * Signifies when start.atlassian.com has finished "loading"
   *
   * Start time: When user receives index.html
   * End time: When all clickable items are rendered (either empty state or both products and activity)
   */
  markContentLoad(pageContent: PageContent, isEmptyState: boolean) {
    // Ignore if start time wasn't recorded
    if (!window.__UCHI_PAGELOAD_START_TIME__) {
      return;
    }

    contentLoadTime[pageContent] = Date.now();
    this.analyticsWebClient.stopApdexEvent({
      additionalAttributes: {
        isEmptyState,
      },
      task: pageContent,
      type: apdexType.INITIAL_LOAD,
    });

    if (contentLoadTime[PageContent.PRODUCTS]) {
      this.analyticsWebClient.stopApdexEvent({
        additionalAttributes: {
          lastLoaded: pageContent,
          referrer: referrerHostName(),
        },
        task: 'pageload',
        type: apdexType.INITIAL_LOAD,
      });

      // Get rid of start time, since we're no longer on the initial load
      delete window.__UCHI_PAGELOAD_START_TIME__;
      contentLoadTime = {};
    }
  }

  async page(name: string, properties = {}) {
    await this.readyPromise;
    await new Promise(resolve => {
      this.analyticsWebClient.sendScreenEvent(
        {
          attributes: {
            ...properties,
            ...this.getAdditionalContext(),
          },
          name,
        },
        resolve,
      );
    });
  }

  /**
   *  Start up E-MAU events in the new analytics pipeline (GAS v3)
   *  See: https://hello.atlassian.net/wiki/spaces/MAU/pages/127745766/DACI+E-MAU+Definition
   */
  async startUIViewedEvent() {
    await this.readyPromise;
    this.analyticsWebClient.startUIViewedEvent();
  }

  async track(subject: string, action: string, attributes = {}) {
    await this.readyPromise;
    await new Promise(resolve => {
      this.analyticsWebClient.sendTrackEvent(
        {
          action,
          actionSubject: subject,
          attributes: {
            ...attributes,
            ...this.getAdditionalContext(),
          },
          source: this.getDefaultSource(),
        },
        resolve,
      );
    });
  }

  async trackOperational(subject: string, action: string, attributes = {}) {
    await this.readyPromise;
    await new Promise(resolve => {
      this.analyticsWebClient.sendOperationalEvent(
        {
          action,
          actionSubject: subject,
          attributes: {
            ...attributes,
            ...this.getAdditionalContext(),
          },
          source: this.getDefaultSource(),
        },
        resolve,
      );
    });
  }

  async ui(subject: string, action: string, attributes = {}, additionalPayload: object = {}) {
    await this.readyPromise;
    await new Promise(resolve => {
      const params: UIEventPayload = {
        action,
        actionSubject: subject,
        attributes: {
          ...attributes,
          ...this.getAdditionalContext(),
        },
        source: this.getDefaultSource(),
        ...additionalPayload,
      };

      this.analyticsWebClient.sendUIEvent(params, resolve);
    });
  }

  private getAdditionalContext() {
    return { ...getAnalyticsContext(), referrer: referrerHostName() };
  }

  private async getUserAccountId(): Promise<string> {
    try {
      return (await this.userStore.readyPromise) || 'default';
    } catch (e) {
      return 'default';
    }
  }

  private initaliseUfo() {
    initUfo(this.analyticsWebClient);
  }

  private async initialiseAnalytics(): Promise<void> {
    const accountId = await this.getUserAccountId();

    this.analyticsWebClient.setTenantInfo(tenantType.NONE, undefined);
    this.analyticsWebClient.setUserInfo(userType.ATLASSIAN_ACCOUNT, accountId);
  }
}
